/**
 * Formatter ESC/POS reusable para recibo térmico 80mm (≈48 chars Font A; 58mm ≈32).
 * Produce una cadena "latin1" (cada char = 1 byte) lista para enviar como RAW por
 * QZ Tray (`{ type:'raw', format:'plain' }`). Un único módulo que reusan el ticket de
 * COCINA (comanda) y el de CIERRE de día — encabezado/cuerpo/corte compartidos.
 *
 * Encoding: seleccionamos CP850 (ESC t 2) y mapeamos los caracteres del español
 * (acentos, ñ, ¿¡, °) a sus bytes CP850 — así "jalapeño"/"término" salen bien en la
 * impresora. Lo no mapeado cae a ASCII o '?'.
 */

const ESC = '\x1B';
const GS = '\x1D';

// Mapa Unicode → byte CP850 para los caracteres del español que no son ASCII.
const CP850: Record<string, number> = {
  'Ç': 0x80, 'ü': 0x81, 'é': 0x82, 'â': 0x83, 'ä': 0x84, 'à': 0x85, 'ç': 0x87,
  'ê': 0x88, 'ë': 0x89, 'è': 0x8a, 'ï': 0x8b, 'î': 0x8c, 'Ä': 0x8e,
  'É': 0x90, 'ô': 0x93, 'ö': 0x94, 'ò': 0x95, 'û': 0x96, 'ù': 0x97, 'Ö': 0x99, 'Ü': 0x9a,
  'á': 0xa0, 'í': 0xa1, 'ó': 0xa2, 'ú': 0xa3, 'ñ': 0xa4, 'Ñ': 0xa5, '¿': 0xa8, '¡': 0xad,
  'Á': 0xb5, 'Í': 0xd6, 'Ó': 0xe0, 'Ú': 0xe9, '°': 0xf8, '€': 0xd5,
};

/** Codifica a bytes CP850 (devuelve string latin1: 1 char = 1 byte). */
function enc(s: string): string {
  let out = '';
  for (const ch of s) {
    const code = ch.charCodeAt(0);
    if (code < 0x80) out += ch;                       // ASCII directo
    else if (CP850[ch] != null) out += String.fromCharCode(CP850[ch]);
    else out += '?';                                  // fuera de CP850
  }
  return out;
}

export type Align = 'l' | 'c' | 'r';
export type Size = 'normal' | 'tall' | 'wide' | 'double';

const SIZE_BYTE: Record<Size, number> = {
  normal: 0x00, tall: 0x01, wide: 0x10, double: 0x11,
};

/** Builder incremental de comandos ESC/POS. `cols` = ancho (48 = 80mm, 32 = 58mm). */
export class EscPos {
  private buf = '';
  constructor(public readonly cols = 48) {}

  raw(s: string): this { this.buf += s; return this; }

  /** Reset + selección de CP850. */
  init(): this { return this.raw(`${ESC}@`).raw(`${ESC}t\x02`); }

  align(a: Align): this { return this.raw(`${ESC}a${String.fromCharCode(a === 'c' ? 1 : a === 'r' ? 2 : 0)}`); }
  bold(on: boolean): this { return this.raw(`${ESC}E${String.fromCharCode(on ? 1 : 0)}`); }
  size(s: Size): this { return this.raw(`${GS}!${String.fromCharCode(SIZE_BYTE[s])}`); }

  /** Una línea de texto (codificada) + salto. */
  text(s = ''): this { return this.raw(enc(s)).raw('\n'); }

  /** Texto largo envuelto al ancho de columnas. */
  wrapped(s: string): this {
    for (const line of wrap(s, this.cols)) this.text(line);
    return this;
  }

  /** Fila de dos columnas: etiqueta a la izquierda, valor a la derecha. */
  lr(left: string, right: string): this {
    const space = Math.max(1, this.cols - left.length - right.length);
    if (left.length + right.length + 1 > this.cols) {
      // No cabe en una línea: izquierda envuelta, valor en su propia línea a la derecha.
      this.wrapped(left);
      return this.text(right.padStart(this.cols));
    }
    return this.text(left + ' '.repeat(space) + right);
  }

  line(ch = '-'): this { return this.text(ch.repeat(this.cols)); }
  feed(n = 1): this { return this.raw('\n'.repeat(n)); }

  /** Avance + corte parcial de papel. */
  cut(): this { return this.feed(3).raw(`${GS}V\x42\x00`); }

  /**
   * Código QR NATIVO ESC/POS (GS ( k) — para autofacturar desde el ticket silencioso.
   * `size` = módulo 1–16 (default 6). Corrección de error nivel L. Centrar antes con align('c').
   */
  qr(data: string, size = 6): this {
    const n = Math.max(1, Math.min(16, size));
    const len = data.length + 3;
    const pL = len & 0xff, pH = (len >> 8) & 0xff;
    // 1) modelo 2  2) tamaño de módulo  3) corrección L  4) guardar datos  5) imprimir
    this.raw(`${GS}(k` + String.fromCharCode(4, 0, 49, 65, 50, 0));
    this.raw(`${GS}(k` + String.fromCharCode(3, 0, 49, 67, n));
    this.raw(`${GS}(k` + String.fromCharCode(3, 0, 49, 69, 48));
    this.raw(`${GS}(k` + String.fromCharCode(pL, pH, 49, 80, 48)).raw(data);
    this.raw(`${GS}(k` + String.fromCharCode(3, 0, 49, 81, 48));
    return this;
  }

  encode(): string { return this.buf; }
}

/** Envuelve `text` a `width` chars respetando palabras. */
export function wrap(text: string, width: number): string[] {
  const words = String(text ?? '').split(/\s+/).filter(Boolean);
  if (!words.length) return [''];
  const lines: string[] = [];
  let cur = '';
  for (const w of words) {
    if (w.length > width) {                  // palabra más larga que la línea: trocear
      if (cur) { lines.push(cur); cur = ''; }
      for (let i = 0; i < w.length; i += width) lines.push(w.slice(i, i + width));
      continue;
    }
    if (!cur) cur = w;
    else if (cur.length + 1 + w.length <= width) cur += ' ' + w;
    else { lines.push(cur); cur = w; }
  }
  if (cur) lines.push(cur);
  return lines;
}

const hhmm = (d: Date) => d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false });

// ===========================================================================
// Ticket de COCINA (comanda) — SIN precios. Solo lo que cocina necesita producir.
// ===========================================================================
export interface KitchenItem {
  qty: number;
  description: string;
  notes?: string | null;
  modifiers?: { name: string }[];
}
export interface KitchenTicketInput {
  brandName: string;
  tableLabel: string;     // "Mesa 5" o "Venta rápida"
  items: KitchenItem[];
  at?: Date;              // hora de envío (default ahora)
  cols?: number;
}

/** ESC/POS del ticket de cocina: mesa grande + ítems + notas/extras + hora. Sin $. */
export function kitchenTicket(input: KitchenTicketInput): string {
  const at = input.at ?? new Date();
  const p = new EscPos(input.cols ?? 48);
  p.init();
  p.align('c').bold(true).size('tall').text('COCINA').size('normal');
  p.text(input.brandName).bold(false);
  p.feed(1).bold(true).size('double').text(input.tableLabel).size('normal').bold(false);
  p.align('l').line();
  for (const it of input.items) {
    p.bold(true).wrapped(`${Number(it.qty)}x ${it.description}`).bold(false);
    if (it.notes) for (const l of wrap(`>> ${it.notes}`, p.cols - 2)) p.text('  ' + l);
    for (const m of it.modifiers ?? []) for (const l of wrap(`+ ${m.name}`, p.cols - 2)) p.text('  ' + l);
  }
  p.line();
  p.align('c').text(`Enviado ${hhmm(at)}`);
  p.cut();
  return p.encode();
}

// ===========================================================================
// Ticket de VENTA (recibo del cliente) — con código de entrega prominente.
// ===========================================================================
export interface SalesTicketItem { qty: number; description: string; lineTotal: number; mods?: string[] }
/** Bloque "Factura esta compra" (autofactura): QR + código + emisor. */
export interface TicketInvoice {
  code: string;           // código legible (Ticket: XXXX-XXXX)
  qrUrl: string;          // URL completa que codifica el QR
  urlLabel?: string;      // URL sin esquema (para imprimir como texto)
  issuerRazon?: string | null;
  issuerRfc?: string | null;
  windowLabel?: string;   // "hasta fin de mes" / "dentro de N días"
  globalNote?: string | null;
}
/** Personalización (V147): logo mono, textos, QR configurable. */
export interface TicketBrandingEsc {
  logoEscposB64?: string | null;   // raster ESC/POS (base64) del logo mono, ya al ancho
  header?: string | null;          // encabezado bajo el logo
  footer?: string | null;          // pie (reemplaza el default)
  customQr?: { url: string; label: string } | null;
}
export interface SalesTicketInput {
  brandName: string;
  delivery?: { code?: string | null; ref?: string | null; statusLabel?: string | null } | null;
  items: SalesTicketItem[];
  subtotal: number; taxTotal: number; total: number; tip: number; grandTotal: number;
  methodLabel: string;
  cash?: { received: number; change: number } | null;
  invoice?: TicketInvoice | null;
  branding?: TicketBrandingEsc | null;
  qrModule?: number;      // tamaño del QR (dots por módulo); default por ancho
  footer?: string;
  at?: Date;
  cols?: number;
  /** 🅐 pieza 1 — PRE-CUENTA (sin cobrar): mismo formato pero encabezado propio y SIN
   *  "TOTAL COBRADO", SIN forma de pago, SIN propina/cambio (el caller ya omite el QR). */
  preCuenta?: boolean;
  /** Fix 11/09 — el tenant ofrece CFDI: en pre-cuenta se imprime "solicita tu
   *  factura al pagar" SIN QR (el QR solo va en el ticket de venta pagada). */
  offersCfdi?: boolean;
  /** V205 (Fidelidad, D2a) — el ticket SELLADO imprime código y avance:
   *  "Tu código ABCD-2346 · 3 de 5 visitas". */
  loyalty?: { line: string; rewardReady?: boolean } | null;
  /** V205 — TICKET DE PROMOCIÓN (cortesía de fidelidad, $0): documento separado, sin
   *  cobro, sin propina/cambio y sin bloque de factura. */
  promo?: boolean;
}

/** Decodifica base64 → string latin1 (1 char = 1 byte) para inyectar bytes RAW. */
function b64ToBytes(b64: string): string {
  try { return typeof atob === 'function' ? atob(b64) : Buffer.from(b64, 'base64').toString('latin1'); }
  catch { return ''; }
}

/** ESC/POS del ticket de venta: marca, código de entrega grande, ítems+$ , totales, pago. */
export function salesTicket(input: SalesTicketInput): string {
  const at = input.at ?? new Date();
  const b = input.branding;
  const p = new EscPos(input.cols ?? 48);
  p.init();
  // Logo mono arriba (raster ESC/POS ya al ancho).
  if (b?.logoEscposB64) p.align('c').raw(b64ToBytes(b.logoEscposB64)).feed(1);
  p.align('c').bold(true).size('tall').text(input.brandName).size('normal').bold(false);
  if (input.preCuenta) p.bold(true).text('PRE-CUENTA · PENDIENTE DE PAGO').bold(false);
  else if (input.promo) p.bold(true).text('TICKET DE PROMOCIÓN').bold(false).text('Cortesía de fidelidad');
  else p.text('Recibo de venta');
  if (b?.header) { p.bold(false); for (const l of wrap(b.header, p.cols)) p.text(l); }
  if (input.delivery?.code) {
    p.feed(1).text('PEDIDO PARA RECOGER');
    p.size('double').bold(true).text(input.delivery.code).size('normal').bold(false);
    if (input.delivery.ref) p.text(`Ref: ${input.delivery.ref}`);
    if (input.delivery.statusLabel) p.bold(true).text(input.delivery.statusLabel).bold(false);
  }
  p.align('l').line();
  for (const it of input.items) {
    p.lr(`${Number(it.qty)}x ${it.description}`, mxn(it.lineTotal));
    for (const m of it.mods ?? []) for (const l of wrap(m, p.cols - 2)) p.text('  ' + l);
  }
  p.line();
  p.lr('Subtotal', mxn(input.subtotal));
  p.lr('IVA (16%)', mxn(input.taxTotal));
  p.bold(true).lr('Total', mxn(input.total)).bold(false);
  if (input.preCuenta) {
    // 🅐 pieza 1 — pre-cuenta: SIN propina, SIN "TOTAL COBRADO", SIN cambio, SIN forma
    // de pago (no hay cobro todavía). Fix 11/09 — leyenda fiscal explícita; y si el
    // tenant emite CFDI, la línea de autofactura SIN QR (el QR solo va en el pagado).
    p.line();
    p.align('c').bold(true).text('CUENTA - NO ES COMPROBANTE FISCAL').bold(false);
    if (input.offersCfdi) p.text('Solicita tu factura al pagar');
  } else if (input.promo) {
    // V205 — cortesía: sin cobro (no entra al corte), sin propina, sin forma de pago.
    p.line();
    p.align('c').bold(true).text('CORTESÍA - SIN COBRO').bold(false);
  } else {
    p.lr('Propina', mxn(input.tip));
    p.line();
    p.bold(true).size('tall').lr('TOTAL COBRADO', mxn(input.grandTotal)).size('normal').bold(false);
    if (input.cash) {
      p.line();
      p.lr('Recibido', mxn(input.cash.received));
      p.bold(true).lr('Cambio', mxn(input.cash.change)).bold(false);
    }
    p.line();
    p.align('c').text(`Pago: ${input.methodLabel}`);
  }
  // V205 (D2a) — código + avance del programa de lealtad ('·' no existe en CP850 → '-').
  if (input.loyalty?.line && !input.preCuenta) {
    p.line();
    p.align('c').bold(true).wrapped(input.loyalty.line.replace(/·/g, '-')).bold(false);
    if (input.loyalty.rewardReady) p.wrapped('Ya tienes tu recompensa: pídela en caja.');
  }
  for (const l of wrap(b?.footer ?? input.footer ?? '¡Gracias por tu visita!', p.cols)) p.text(l);
  const qrModule = input.qrModule ?? (input.cols === 32 ? 5 : 6);
  // Bloque de autofactura: QR nativo + código + emisor + plazo (si el toggle lo incluyó).
  if (input.invoice && !input.promo) {
    const inv = input.invoice;
    p.line();
    p.bold(true).text('FACTURA ESTA COMPRA').bold(false);
    p.qr(inv.qrUrl, qrModule);
    p.text(`Ticket: ${inv.code}`);
    if (inv.urlLabel) p.wrapped(inv.urlLabel);
    if (inv.globalNote) p.wrapped(inv.globalNote);
    if (inv.issuerRazon || inv.issuerRfc) p.wrapped(`Emisor: ${inv.issuerRazon ?? ''} RFC ${inv.issuerRfc ?? ''}`);
    if (inv.windowLabel) p.wrapped(inv.windowLabel);   // V153: el plazo se redacto mas largo -> envolver
  }
  // QR configurable (fidelidad/Instagram/…): apilado bajo el de factura, con su leyenda.
  if (b?.customQr?.url) {
    p.line();
    p.qr(b.customQr.url, qrModule);
    if (b.customQr.label) p.bold(true).wrapped(b.customQr.label).bold(false);
  }
  p.text(at.toLocaleString('es-MX'));
  p.cut();
  return p.encode();
}

// ===========================================================================
// Comprobante de CANCELACIÓN — sin valor fiscal.
// ===========================================================================
export interface CancelTicketInput {
  brandName: string; folio: string; cancelledBy?: string | null; reason?: string | null;
  items: { qty: number; description: string }[];
  branding?: TicketBrandingEsc | null;
  at?: Date; cols?: number;
}
export function cancelTicket(input: CancelTicketInput): string {
  const at = input.at ?? new Date();
  const b = input.branding;
  const p = new EscPos(input.cols ?? 48);
  p.init();
  if (b?.logoEscposB64) p.align('c').raw(b64ToBytes(b.logoEscposB64)).feed(1);
  p.align('c').bold(true).size('tall').text('CANCELACION').size('normal').bold(false);
  p.text(input.brandName);
  if (b?.header) for (const l of wrap(b.header, p.cols)) p.text(l);
  p.text(`Folio ${input.folio}`);
  p.align('l').line();
  for (const it of input.items) p.text(`${Number(it.qty)}x ${it.description}`);
  p.line();
  if (input.cancelledBy) p.lr('Cancelo', input.cancelledBy);
  if (input.reason) p.wrapped(`Motivo: ${input.reason}`);
  p.line();
  p.align('c');
  if (b?.footer) for (const l of wrap(b.footer, p.cols)) p.text(l);
  p.text(at.toLocaleString('es-MX'));
  p.text('Comprobante sin valor fiscal');
  p.cut();
  return p.encode();
}

// ===========================================================================
// Ticket de CIERRE de día / turno — corte de caja en formato recibo.
// ===========================================================================
const mxn = (v: any) =>
  `$${Number(v ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// PINK #2 PR C — crédito/débito separados; 'CARD' legacy = sin especificar (ASCII para ESC/POS).
const METHOD_LABEL: Record<string, string> = {
  CASH: 'Efectivo', CARD_CREDIT: 'Tarjeta credito', CARD_DEBIT: 'Tarjeta debito',
  CARD: 'Tarjeta (s/e)', TRANSFER: 'Transferencia', ONLINE: 'En linea',
};

export interface CashCloseTicketInput {
  brandName: string;
  dateLabel: string;       // "lunes, 22 de junio de 2026"
  shiftLabel: string;      // "Día completo" / "Turno 8:00 a 16:59"
  byMethod: { method: string; sales: number; tips: number; orders: number; collected: number }[];
  totals: {
    subtotal: number; taxTotal: number; sales: number; tips: number; collected: number; orders: number;
    // §30 fast-follow — arqueo de efectivo (opcionales: solo se imprimen si hubo redondeo).
    roundingDiff?: number; cashCollected?: number; expectedCash?: number;
    // PINK #2 PR C — propina en efectivo (la que el mesero se lleva en mano).
    cashTips?: number;
  };
  at?: Date;
  cols?: number;
}

/** ESC/POS del corte de caja: ventas por método + propinas + totales. */
export function cashCloseTicket(input: CashCloseTicketInput): string {
  const at = input.at ?? new Date();
  const p = new EscPos(input.cols ?? 48);
  p.init();
  p.align('c').bold(true).size('tall').text('CORTE DE CAJA').size('normal');
  p.text(input.brandName).bold(false);
  p.text(input.dateLabel);
  p.text(input.shiftLabel);
  p.align('l').line();
  for (const r of input.byMethod) {
    if (!r.orders && !r.collected) continue;          // omite métodos sin movimiento
    p.bold(true).text(METHOD_LABEL[r.method] ?? r.method).bold(false);
    p.lr('  Ventas', mxn(r.sales));
    p.lr('  Propinas', mxn(r.tips));
    p.lr(`  Órdenes (${Number(r.orders)})`, mxn(r.collected));
  }
  p.line();
  p.lr('Subtotal', mxn(input.totals.subtotal));
  p.lr('IVA', mxn(input.totals.taxTotal));
  p.lr('Ventas (c/IVA)', mxn(input.totals.sales));
  p.lr('Propinas', mxn(input.totals.tips));
  // PINK #2 PR C — propina en efectivo aparte (la que el mesero se lleva en mano).
  if (Number(input.totals.cashTips) > 0) p.lr('  de eso en efectivo', mxn(input.totals.cashTips));
  p.lr('# Órdenes', String(Number(input.totals.orders)));
  p.line();
  p.bold(true).size('tall').lr('TOTAL', mxn(input.totals.collected)).size('normal').bold(false);
  // §30 fast-follow — ARQUEO de la caja de efectivo. Solo cuando hubo diferencial de
  // redondeo: el efectivo físico en el cajón = efectivo cobrado − diferencial. NO
  // reemplaza el TOTAL (venta): responde "cuánto debe haber en la caja".
  if (Number(input.totals.roundingDiff) > 0) {
    p.line();
    p.lr('Efectivo cobrado', mxn(input.totals.cashCollected));
    p.lr('Diferencial redondeo', `-${mxn(input.totals.roundingDiff)}`);
    p.bold(true).size('tall').lr('EFECTIVO EN CAJA', mxn(input.totals.expectedCash)).size('normal').bold(false);
  }
  p.line();
  p.align('c').text(`Generado ${at.toLocaleString('es-MX')}`);
  p.cut();
  return p.encode();
}
